import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import RedirectKeepQuery from './components/RedirectKeepQuery';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/theme.css';
import { ThemeProvider } from './context/ThemeContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { PWAProvider } from './context/PWAContext';
import PWAInstallPrompt, { PWAUpdatePrompt, OfflineIndicator } from './components/PWAInstallPrompt';
import Login from './components/Login';
import api from './services/api';

// Lazy-load all route components. Only the visited page downloads its JS
const Dashboard = lazy(() => import('./components/Dashboard'));
const RegistrantDashboard = lazy(() => import('./components/RegistrantDashboard'));
const RegistrantDashboardV2 = lazy(() => import('./components/RegistrantDashboardV2'));
const SignupPage = lazy(() => import('./components/SignupPage'));
const HomePage = lazy(() => import('./marketing/HomePage'));
const AudiencePage = lazy(() => import('./marketing/AudiencePage'));
const WhoWeArePage = lazy(() => import('./marketing/WhoWeArePage'));
const ImpactPage = lazy(() => import('./marketing/ImpactPage'));
const OpportunitiesPage = lazy(() => import('./marketing/OpportunitiesPage'));
const NewsPage = lazy(() => import('./marketing/NewsPage'));
const NaseniCasePage = lazy(() => import('./marketing/NaseniCasePage'));
const PricingPage = lazy(() => import('./marketing/PricingPage'));
const DeviceRegistration = lazy(() => import('./components/DeviceRegistration'));
const AdvancedFacilitySearch = lazy(() => import('./components/AdvancedFacilitySearch'));
const AdvancedDeviceSearch = lazy(() => import('./components/AdvancedDeviceSearch'));
const AdminDeviceApproval = lazy(() => import('./components/AdminDeviceApproval'));
const AdminSettings = lazy(() => import('./components/admin/AdminSettings'));
const PublicAuditView = lazy(() => import('./components/PublicAuditView'));
const SiteCapturePage = lazy(() => import('./components/SiteCapturePage'));
const PortfolioInvitePage = lazy(() => import('./components/portfolio/PortfolioInvitePage'));
const PortfolioPublicView = lazy(() => import('./components/portfolio/PortfolioPublicView'));
const PortfolioReportPage = lazy(() => import('./components/portfolio/PortfolioReportPage'));

// Route-level loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #E5E7EB', borderTopColor: '#0D6B4B', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <div style={{ fontSize: 13, color: '#6B7280' }}>Loading...</div>
    </div>
  </div>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token with backend to ensure session is still valid
  const validateToken = useCallback(async (token) => {
    try {
      // Make a lightweight request to verify token is still valid
      const response = await api.get('/auth/verify');
      return response.data.valid !== false;
    } catch (error) {
      // If 401 or network error, token might be invalid
      if (error.response?.status === 401) {
        return false;
      }
      // For network errors (offline), trust the local token
      return true;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Check if user is already logged in
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      if (token && user) {
        try {
          const userData = JSON.parse(user);

          // Validate token with backend (but don't block offline usage)
          const isValid = await validateToken(token);

          if (isValid) {
            setIsAuthenticated(true);
            setUserRole(userData.role);
          } else {
            // Token expired or invalid - clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (e) {
          // Invalid JSON in localStorage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [validateToken]);

  const handleLogin = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setIsAuthenticated(true);
    setUserRole(user.role);

    // Store registrant_id for telemetry and other registrant-scoped API calls
    if (user.registrant_id) {
      localStorage.setItem('registrant_id', String(user.registrant_id));
    }

    // Store trial info if present in login response
    if (user.trial) {
      localStorage.setItem('gf_trial_info', JSON.stringify(user.trial));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('registrant_id');
    localStorage.removeItem('gf_trial_info');
    setIsAuthenticated(false);
    setUserRole(null);
    toast.info('Logged out successfully');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <ThemeProvider>
      <OrganizationProvider>
        <PWAProvider>
          <Router>
            <div className="App">
              {/* PWA Components. The install/update prompts belong inside the
                  authenticated app, not on the public marketing site, where an
                  install modal would cover the hero and clash with the brand. */}
              <OfflineIndicator />
              {isAuthenticated && <PWAInstallPrompt />}
              {isAuthenticated && <PWAUpdatePrompt />}

              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Login Route */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <RedirectKeepQuery to={userRole === 'registrant' ? "/registrant-dashboard" : "/dashboard"} />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />

          {/* Admin Dashboard Route. Any non-registrant role (admin,
              superadmin, verifier, issuer, anything else) lands here.
              Only the 'registrant' role is redirected away. This avoids
              the infinite-redirect loop the previous code triggered when
              a role like 'superadmin' matched neither exact check and
              the two routes bounced against each other. */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                userRole === 'registrant' ? (
                  <RedirectKeepQuery to="/registrant-dashboard" />
                ) : (
                  <Dashboard onLogout={handleLogout} />
                )
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Registrant Dashboard Route (Modern Sidebar) */}
          <Route
            path="/registrant-dashboard"
            element={
              isAuthenticated ? (
                userRole === 'registrant' ? (
                  <RegistrantDashboardV2 onLogout={handleLogout} />
                ) : (
                  <RedirectKeepQuery to="/dashboard" />
                )
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Registrant Dashboard Legacy (Original Tab Layout) */}
          <Route
            path="/registrant-dashboard-legacy"
            element={
              isAuthenticated ? (
                userRole === 'registrant' ? (
                  <RegistrantDashboard onLogout={handleLogout} />
                ) : (
                  <RedirectKeepQuery to="/dashboard" />
                )
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Public Registration Page */}
          <Route
            path="/register"
            element={<SignupPage />}
          />

          {/* Device Registration via QR Code (Public) */}
          <Route
            path="/device-registration"
            element={<DeviceRegistration />}
          />

          {/* Advanced Search Routes */}
          <Route
            path="/search/facilities"
            element={
              isAuthenticated ? (
                <AdvancedFacilitySearch />
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          <Route
            path="/search/devices"
            element={
              isAuthenticated ? (
                <AdvancedDeviceSearch />
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Admin Device Approval Route. Any non-registrant role allowed
              (admin, superadmin, verifier, issuer) so superadmin users do
              not bounce off the strict 'admin' check. */}
          <Route
            path="/admin/device-approval"
            element={
              isAuthenticated ? (
                userRole === 'registrant' ? (
                  <RedirectKeepQuery to="/registrant-dashboard" />
                ) : (
                  <AdminDeviceApproval />
                )
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Admin Settings Route. Same broadened check as above. */}
          <Route
            path="/admin/settings"
            element={
              isAuthenticated ? (
                userRole === 'registrant' ? (
                  <RedirectKeepQuery to="/registrant-dashboard" />
                ) : (
                  <AdminSettings />
                )
              ) : (
                <RedirectKeepQuery to="/login" />
              )
            }
          />

          {/* Site capture link (NO AUTH): the link itself is the credential,
              verified server side by site_capture_api.py. */}
          <Route
            path="/capture/:token"
            element={<SiteCapturePage />}
          />

          {/* Portfolio invitation (NO AUTH): says who is asking before the
              registrant signs in; the token waits in localStorage and is
              answered on Data sharing. portfolio_api.public_invitation. */}
          <Route
            path="/portfolio-invite/:token"
            element={<PortfolioInvitePage />}
          />

          {/* Portfolio funder link (NO AUTH): HMAC-validated server side by
              portfolio_api.public_portfolio. */}
          <Route
            path="/portfolio/share/:token"
            element={<PortfolioPublicView />}
          />
          {/* ESG report from portfolio analytics: public, verified by its SHA-256 fingerprint. */}
          <Route
            path="/portfolio/report/:sha"
            element={<PortfolioReportPage />}
          />

          {/* Public Audit Share Route (NO AUTH) */}
          <Route
            path="/audit/:token"
            element={<PublicAuditView />}
          />

          {/* Public marketing audience pages (always public) */}
          <Route
            path="/for/:segment"
            element={<AudiencePage />}
          />

          {/* Public marketing site pages */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/who-we-are" element={<WhoWeArePage />} />
          <Route path="/impact" element={<ImpactPage />} />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/case-studies/naseni" element={<NaseniCasePage />} />

          {/* Home Route - Flagship marketing site for visitors, redirect for authenticated users */}
          <Route
            path="/"
            element={
              isAuthenticated
                ? <RedirectKeepQuery to={userRole === 'registrant' ? "/registrant-dashboard" : "/dashboard"} />
                : <HomePage />
            }
          />
              </Routes>
              </Suspense>
            </div>
          </Router>
        </PWAProvider>
      </OrganizationProvider>
    </ThemeProvider>
  );
}

export default App;
